import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import RequirementsQuestionnaire from '../components/RequirementsQuestionnaire';
import { clientLogger } from '../utils/logger';

/**
 * SurveyFlow component that handles UUID-based survey routing
 * Supports bookmarking and browser back button navigation
 */
const SurveyFlow = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [surveyUuid, setSurveyUuid] = useState(uuid);
    const [surveyData, setSurveyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentStep, setCurrentStep] = useState(0);

    // Generate UUID if not provided
    useEffect(() => {
        if (!uuid) {
            const newUuid = uuidv4();
            clientLogger.info('SurveyFlow: Generated new survey UUID', { uuid: newUuid });
            setSurveyUuid(newUuid);
            
            // Update URL without triggering navigation
            navigate(`/survey/${newUuid}`, { replace: true });
        } else {
            setSurveyUuid(uuid);
        }
    }, [uuid, navigate]);

    // Load survey data when UUID is available
    useEffect(() => {
        if (!surveyUuid) return;

        const loadSurveyData = async () => {
            try {
                setLoading(true);
                
                // Try to load existing survey data from localStorage first
                const localStorageKey = `survey_${surveyUuid}`;
                const savedData = localStorage.getItem(localStorageKey);
                
                if (savedData) {
                    const parsed = JSON.parse(savedData);
                    setSurveyData(parsed);
                    setCurrentStep(parsed.currentStep || 0);
                    clientLogger.info('SurveyFlow: Loaded survey data from localStorage', { 
                        uuid: surveyUuid, 
                        step: parsed.currentStep 
                    });
                } else {
                    // Try to load from server
                    try {
                        const response = await fetch(`/api/survey/${surveyUuid}`, {
                            credentials: 'include'
                        });
                        
                        if (response.ok) {
                            const serverData = await response.json();
                            setSurveyData(serverData);
                            setCurrentStep(serverData.currentStep || 0);
                            
                            // Cache in localStorage
                            localStorage.setItem(localStorageKey, JSON.stringify(serverData));
                            
                            clientLogger.info('SurveyFlow: Loaded survey data from server', { 
                                uuid: surveyUuid, 
                                step: serverData.currentStep 
                            });
                        } else if (response.status === 404) {
                            // New survey - initialize with default data
                            const newSurveyData = {
                                uuid: surveyUuid,
                                createdAt: new Date().toISOString(),
                                currentStep: 0,
                                responses: {},
                                organization: null,
                                user: null
                            };
                            
                            setSurveyData(newSurveyData);
                            setCurrentStep(0);
                            
                            // Save to localStorage
                            localStorage.setItem(localStorageKey, JSON.stringify(newSurveyData));
                            
                            clientLogger.info('SurveyFlow: Created new survey', { uuid: surveyUuid });
                        } else {
                            throw new Error(`Server responded with ${response.status}`);
                        }
                    } catch (serverError) {
                        clientLogger.warn('SurveyFlow: Failed to load from server, using local fallback', { 
                            error: serverError.message 
                        });
                        
                        // Fallback to new survey if server fails
                        const fallbackData = {
                            uuid: surveyUuid,
                            createdAt: new Date().toISOString(),
                            currentStep: 0,
                            responses: {},
                            organization: null,
                            user: null
                        };
                        
                        setSurveyData(fallbackData);
                        setCurrentStep(0);
                        localStorage.setItem(localStorageKey, JSON.stringify(fallbackData));
                    }
                }
            } catch (error) {
                clientLogger.error('SurveyFlow: Error loading survey data', { 
                    uuid: surveyUuid, 
                    error: error.message 
                });
                
                // Use fallback data on any error
                const errorFallbackData = {
                    uuid: surveyUuid,
                    createdAt: new Date().toISOString(),
                    currentStep: 0,
                    responses: {},
                    organization: null,
                    user: null,
                    error: true
                };
                
                setSurveyData(errorFallbackData);
                setCurrentStep(0);
            } finally {
                setLoading(false);
            }
        };

        loadSurveyData();
    }, [surveyUuid]);

    // Handle hash-based navigation within survey
    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (hash && !isNaN(parseInt(hash))) {
            const step = parseInt(hash);
            setCurrentStep(step);
            
            // Update survey data with current step
            if (surveyData) {
                const updatedData = { ...surveyData, currentStep: step };
                setSurveyData(updatedData);
                
                // Save to localStorage
                const localStorageKey = `survey_${surveyUuid}`;
                localStorage.setItem(localStorageKey, JSON.stringify(updatedData));
            }
        }
    }, [location.hash, surveyData, surveyUuid]);

    // Save survey progress
    const saveSurveyProgress = async (updatedData) => {
        if (!surveyUuid) return;

        try {
            const dataToSave = {
                ...surveyData,
                ...updatedData,
                lastUpdated: new Date().toISOString()
            };
            
            setSurveyData(dataToSave);
            
            // Save to localStorage immediately
            const localStorageKey = `survey_${surveyUuid}`;
            localStorage.setItem(localStorageKey, JSON.stringify(dataToSave));
            
            // Try to save to server (non-blocking)
            try {
                await fetch(`/api/survey/${surveyUuid}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify(dataToSave)
                });
                
                clientLogger.debug('SurveyFlow: Saved survey data to server', { uuid: surveyUuid });
            } catch (serverError) {
                clientLogger.warn('SurveyFlow: Failed to save to server, data saved locally', { 
                    uuid: surveyUuid,
                    error: serverError.message 
                });
            }
        } catch (error) {
            clientLogger.error('SurveyFlow: Error saving survey progress', { 
                uuid: surveyUuid, 
                error: error.message 
            });
        }
    };

    // Handle step navigation
    const navigateToStep = (step) => {
        setCurrentStep(step);
        
        // Update URL hash for bookmarking
        navigate(`/survey/${surveyUuid}#${step}`, { replace: true });
        
        // Save progress
        saveSurveyProgress({ currentStep: step });
        
        clientLogger.info('SurveyFlow: Navigated to step', { uuid: surveyUuid, step });
    };

    // Handle survey completion
    const handleSurveyComplete = async (completedData) => {
        try {
            const finalData = {
                ...surveyData,
                ...completedData,
                completedAt: new Date().toISOString(),
                status: 'completed'
            };
            
            await saveSurveyProgress(finalData);
            
            clientLogger.info('SurveyFlow: Survey completed', { uuid: surveyUuid });
            
            // Redirect to completion page or dashboard
            navigate(`/survey/${surveyUuid}/complete`);
        } catch (error) {
            clientLogger.error('SurveyFlow: Error completing survey', { 
                uuid: surveyUuid, 
                error: error.message 
            });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading survey...</p>
                    {surveyUuid && (
                        <p className="text-sm text-gray-400 mt-2">Survey ID: {surveyUuid}</p>
                    )}
                </div>
            </div>
        );
    }

    if (!surveyData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Survey</h2>
                    <p className="text-gray-600">Unable to load survey data.</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
            {/* Survey Header with UUID */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-4xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">
                                Requirements Survey
                            </h1>
                            <p className="text-sm text-gray-500">
                                Survey ID: {surveyUuid}
                            </p>
                        </div>
                        <div className="text-sm text-gray-500">
                            Step {currentStep + 1}
                        </div>
                    </div>
                </div>
            </div>

            {/* Survey Content */}
            <div className="max-w-4xl mx-auto px-4 py-6">
                <RequirementsQuestionnaire
                    user={surveyData.user}
                    organization={surveyData.organization}
                    surveyUuid={surveyUuid}
                    currentStep={currentStep}
                    onStepChange={navigateToStep}
                    onSurveyComplete={handleSurveyComplete}
                    onSaveProgress={saveSurveyProgress}
                    initialData={surveyData}
                />
            </div>

            {/* Footer with sharing/bookmark info */}
            <div className="bg-gray-50 border-t">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="text-center text-sm text-gray-600">
                        <p>
                            💡 <strong>Bookmark this page</strong> to return to your survey anytime. 
                            Your progress is automatically saved.
                        </p>
                        <p className="mt-1">
                            Share this link with team members: 
                            <code className="bg-gray-200 px-2 py-1 rounded ml-2 text-xs">
                                {window.location.origin}/survey/{surveyUuid}
                            </code>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SurveyFlow; 